/**
 * RANKED — who did the most, and who did the least.
 *
 * A different JOB from the trend chart, so a different form. Trend answers "when did
 * this happen"; this answers "compare magnitude across people", which is a sorted bar
 * chart with ONE hue. Length carries the comparison, so it scales to every person
 * selected without a legend and without colour — where the trend chart hits an 8-colour
 * ceiling and folds the rest into "Other", hiding two thirds of a 34-person selection.
 *
 * COLOUR ENCODES NOTHING HERE and that is deliberate. Shading bars by their own value
 * would double-encode what length already says, and giving each person a hue would be
 * the categorical trap this form exists to avoid.
 *
 * THREE NUMBERS, BECAUSE THE RAW TOTAL MISLEADS. Someone on leave for three weeks has
 * a small total and looks idle; someone running bulk edits has a huge one from few
 * actual actions. `Active days` separates presence from volume, and `per active day`
 * separates intensity from presence. Ranking by any of the three is one click, because
 * which one is "most active" is a judgement the reader has to make, not one the chart
 * should make silently.
 *
 * Derived entirely from the /series day buckets already fetched — no extra endpoint,
 * no extra grant, and the cost stays one row per person per day.
 */
import { useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip,
  XAxis, YAxis,
} from 'recharts';
import { SeriesPoint } from '../../../services/userHistoryReport.service';

/** One hue; see the header note on why colour carries no information. */
const BAR_LIGHT = '#2a78d6';
const BAR_DARK = '#3987e5';
/** The de-emphasis step, for a person with no activity at all in the window. */
const ZERO_LIGHT = '#d4d4d0';
const ZERO_DARK = '#44443f';

export type RankMetric = 'total' | 'perDay' | 'activeDays';

const METRICS: { id: RankMetric; label: string; help: string }[] = [
  { id: 'total', label: 'Total hits', help: 'Raw volume over the window.' },
  { id: 'perDay', label: 'Per active day', help: 'Volume on the days they actually worked — intensity, not presence.' },
  { id: 'activeDays', label: 'Active days', help: 'Days with any activity at all — presence, not volume.' },
];

export interface RankedUser {
  name: string;
  total: number;
  activeDays: number;
  perDay: number;
}

/** Per-person totals from day-bucketed series points.
 *
 *  `activeDays` counts DISTINCT buckets with a non-zero count, so a person is not
 *  credited twice for a day their two identities both wrote on. */
export function rankUsers(points: SeriesPoint[], metric: RankMetric = 'total'): RankedUser[] {
  const totals = new Map<string, number>();
  const days = new Map<string, Set<string>>();
  for (const p of points) {
    if (p.hits <= 0) continue;
    totals.set(p.user_name, (totals.get(p.user_name) ?? 0) + p.hits);
    if (!days.has(p.user_name)) days.set(p.user_name, new Set());
    days.get(p.user_name)!.add(String(p.bucket));
  }
  const rows = [...totals.entries()].map(([name, total]) => {
    const activeDays = days.get(name)?.size ?? 0;
    return {
      name,
      total,
      activeDays,
      perDay: activeDays ? Math.round(total / activeDays) : 0,
    };
  });
  // Descending, name as the tiebreak so equal values do not reorder between renders.
  return rows.sort((a, b) => (b[metric] - a[metric]) || a.name.localeCompare(b.name));
}

const num = (v: number) => Number(v ?? 0).toLocaleString();

export interface RankedUsersChartProps {
  points: SeriesPoint[];
  /** Everyone selected, so people with NO activity still appear, at zero. Their
   *  absence from the data is the finding; dropping them hides it. */
  allUserNames?: string[];
  dark?: boolean;
  loading?: boolean;
}

const RankedUsersChart: React.FC<RankedUsersChartProps> = ({
  points, allUserNames, dark = false, loading,
}) => {
  const [metric, setMetric] = useState<RankMetric>('total');

  const rows = useMemo(() => {
    const ranked = rankUsers(points, metric);
    if (!allUserNames?.length) return ranked;
    const seen = new Set(ranked.map((r) => r.name));
    const missing = allUserNames
      .filter((n) => !seen.has(n))
      .map((name) => ({ name, total: 0, activeDays: 0, perDay: 0 }));
    return [...ranked, ...missing.sort((a, b) => a.name.localeCompare(b.name))];
  }, [points, metric, allUserNames]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        Loading…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        No activity in this range.
      </div>
    );
  }

  const active = METRICS.find((m) => m.id === metric)!;
  // Each bar gets a fixed band so 30 people stay readable; the container scrolls
  // rather than squeezing rows until the names collide.
  const height = Math.max(180, rows.length * 30 + 40);

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="shrink-0 flex items-center gap-2 pb-2">
        <span className="text-xs text-gray-500 dark:text-gray-400">Rank by</span>
        {METRICS.map((m) => (
          <button
            key={m.id}
            type="button"
            aria-pressed={metric === m.id}
            title={m.help}
            onClick={() => setMetric(m.id)}
            className={`px-2 py-0.5 text-xs rounded border ${
              metric === m.id
                ? 'border-blue-600 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
            }`}
          >
            {m.label}
          </button>
        ))}
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">{active.help}</span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={rows}
            layout="vertical"
            margin={{ top: 4, right: 72, left: 8, bottom: 4 }}
            barCategoryGap={6}
          >
            <CartesianGrid horizontal={false} strokeDasharray="3 3"
              className="stroke-gray-200 dark:stroke-gray-700" />
            <XAxis
              type="number"
              tickFormatter={num}
              className="text-gray-600 dark:text-gray-400"
              tick={{ fill: 'currentColor', fontSize: 11 }}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={168}
              interval={0}
              className="text-gray-700 dark:text-gray-300"
              tick={{ fill: 'currentColor', fontSize: 12 }}
            />
            <Tooltip
              cursor={{ fill: dark ? '#ffffff14' : '#0000000a' }}
              contentStyle={{
                backgroundColor: dark ? '#1a1a19' : '#ffffff',
                border: `1px solid ${dark ? '#3f3f3b' : '#e5e7eb'}`,
                borderRadius: '0.5rem',
                color: dark ? '#ffffff' : '#0b0b0b',
                fontSize: 12,
              }}
              // All three numbers on hover regardless of which one is ranking, so the
              // reader can see presence and intensity behind any ordering.
              formatter={(_v: any, _n: any, item: any) => {
                const r = item?.payload as RankedUser;
                return [
                  `${num(r.total)} hits · ${num(r.activeDays)} active days · ${num(r.perDay)}/day`,
                  '',
                ];
              }}
            />
            <Bar dataKey={metric} radius={[0, 4, 4, 0]} isAnimationActive={false}>
              {rows.map((r) => (
                <Cell
                  key={r.name}
                  // A true zero wears the de-emphasis step, not the series hue: "did
                  // nothing in this window" is a different statement from "did little".
                  fill={r[metric] === 0
                    ? (dark ? ZERO_DARK : ZERO_LIGHT)
                    : (dark ? BAR_DARK : BAR_LIGHT)}
                />
              ))}
              {/* Direct labels: the exact number beside every bar, so the chart never
                  needs a second lookup and the light-surface contrast WARN is relieved. */}
              <LabelList
                dataKey={metric}
                position="right"
                formatter={(v: unknown) => num(Number(v ?? 0))}
                className="fill-gray-700 dark:fill-gray-300"
                style={{ fontSize: 11 }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RankedUsersChart;
